import { Fragment, ReactNode, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ChevronDown, ExternalLink, MessageCircle, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import CircularProgress from "@/components/common/CircularProgress";
import { comunidadAdapter, ComunidadNewsFeed, ComunidadNewsItem } from "@/hooks/api/comunidad";
import { toastUtils } from "@/hooks/useToast";
import { RichContent } from "./components/RichContent";
import { isRichHtml } from "./richText";
import { HomeArticleDialog, getHomeArticleId } from "./components/HomeArticleDialog";

const INLINE_IMAGE_RE = /^!\[(.*)\]\((.*)\)$/;
/** 📌 al inicio de un párrafo lo resalta en un recuadro con onda (ej. el detalle de un
 *  taller/evento puntual) — el marcador se saca del texto, no se muestra. */
const HIGHLIGHT_MARKER = "📌";

/** `**negrita**` y `[texto](url)` dentro de un mismo párrafo — un solo pase de regex para no
 *  perder el orden entre ambos. Todo lo demás queda como texto plano. */
function renderInline(text: string, keyPrefix: string) {
  const regex = /\*\*(.+?)\*\*|\[(.+?)\]\((.+?)\)/g;
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let i = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(<Fragment key={`${keyPrefix}-t${i++}`}>{text.slice(lastIndex, match.index)}</Fragment>);
    }
    if (match[1] !== undefined) {
      nodes.push(<strong key={`${keyPrefix}-b${i++}`}>{match[1]}</strong>);
    } else {
      nodes.push(
        <a
          key={`${keyPrefix}-l${i++}`}
          href={match[3]}
          target="_blank"
          rel="noreferrer"
          className="font-semibold text-[#665090] underline underline-offset-2 hover:text-[#4A3D73]"
        >
          {match[2]}
        </a>,
      );
    }
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    nodes.push(<Fragment key={`${keyPrefix}-t${i++}`}>{text.slice(lastIndex)}</Fragment>);
  }
  return nodes;
}

/** Formatea el texto (sin resumir) de una novedad: **negrita** en línea, párrafos que
 *  empiezan con ⚠️ o "Importante" resaltados en un recuadro de aviso (igual que en Home),
 *  párrafos que empiezan con 📌 resaltados en un recuadro con onda, y un párrafo con sintaxis
 *  `![alt](url)` se renderiza como una imagen intercalada — así se puede meter una captura en
 *  cualquier punto del paso a paso, no solo al final. */
function FormattedBody({ body }: { body: string }) {
  const paragraphs = body.split(/\n{2,}/);
  return (
    <div className="flex w-full flex-col gap-3 text-sm leading-relaxed text-gray-700">
      {paragraphs.map((paragraph, i) => {
        const imageMatch = paragraph.trim().match(INLINE_IMAGE_RE);
        if (imageMatch) {
          const [, alt, src] = imageMatch;
          return (
            <img
              key={i}
              src={src}
              alt={alt}
              className="mx-auto h-auto max-h-[480px] w-auto max-w-full rounded-xl border border-[#E3D9FF] object-contain"
            />
          );
        }
        const trimmed = paragraph.trim();
        const isWarning = /^(⚠️|\*\*importante)/i.test(trimmed);
        const isHighlight = trimmed.startsWith(HIGHLIGHT_MARKER);
        const cleanParagraph = isHighlight ? trimmed.slice(HIGHLIGHT_MARKER.length).trimStart() : paragraph;
        const rendered = renderInline(cleanParagraph, `p${i}`);
        if (isHighlight) {
          return (
            <div
              key={i}
              className="whitespace-pre-line rounded-2xl border-2 border-[#B9E12F] bg-[#F6FBE3] px-5 py-4 text-[#2D2049] shadow-[0_4px_14px_rgba(185,225,47,0.25)]"
            >
              {rendered}
            </div>
          );
        }
        return isWarning ? (
          <div key={i} className="whitespace-pre-line rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800">
            {rendered}
          </div>
        ) : (
          <p key={i} className="m-0 whitespace-pre-line">
            {rendered}
          </p>
        );
      })}
    </div>
  );
}

interface NewsArchiveContentProps {
  feed: ComunidadNewsFeed;
}

const PAGE_SIZE = 10;

/** Convierte un link "normal" de YouTube/Loom a su URL de embed — si no reconoce el
 *  formato, devuelve la URL tal cual (varios hosts de video aceptan el link directo en un
 *  iframe igual). */
function toEmbedUrl(url: string): string {
  const youtubeMatch = url.match(/(?:youtu\.be\/|youtube\.com\/watch\?v=)([\w-]+)/);
  if (youtubeMatch) return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
  const loomMatch = url.match(/loom\.com\/share\/([\w-]+)/);
  if (loomMatch) return `https://www.loom.com/embed/${loomMatch[1]}`;
  return url;
}

const FEED_CONFIG: Record<
  ComunidadNewsFeed,
  { title: string; icon: typeof Sparkles; iconBg: string; iconColor: string }
> = {
  psi_platform: {
    title: "Novedades Psi Platform",
    icon: Sparkles,
    iconBg: "bg-[#E9E0FC]",
    iconColor: "text-[#665090]",
  },
  institutional: {
    title: "Institucional",
    icon: MessageCircle,
    iconBg: "bg-[#EDE6FA]",
    iconColor: "text-[#4A3D73]",
  },
};

/** Reservorio completo de un tipo de novedad — a diferencia del "spoiler" de la sidebar
 *  (solo título), acá cada novedad se puede desplegar con un click para ver su desarrollo
 *  completo. Carga de a tandas (más nueva primero) en vez de traer todo el historial de
 *  una — "Cargar más" pide la página siguiente. */
export const NewsArchiveContent = ({ feed }: NewsArchiveContentProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const targetId = searchParams.get("item");
  const [news, setNews] = useState<ComunidadNewsItem[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  // Artículo de la Home abierto en la ventana emergente (ver HomeArticleDialog).
  const [openArticleId, setOpenArticleId] = useState<string | null>(null);
  const config = FEED_CONFIG[feed];
  const Icon = config.icon;
  /** Evita repetir el auto-despliegue + scroll si la persona colapsa el ítem a mano después. */
  const autoHandledTargetRef = useRef<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setNews([]);
    setPage(1);
    comunidadAdapter
      .getNewsPage(feed, 1, PAGE_SIZE)
      .then((result) => {
        setNews(result.data);
        setHasMore(result.hasMore);
      })
      .catch(() => toastUtils.error("No pudimos cargar el archivo. Probá de nuevo en un rato."))
      .finally(() => setLoading(false));
  }, [feed]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const result = await comunidadAdapter.getNewsPage(feed, nextPage, PAGE_SIZE);
      setNews((prev) => [...prev, ...result.data]);
      setHasMore(result.hasMore);
      setPage(nextPage);
    } catch {
      toastUtils.error("No pudimos cargar más. Probá de nuevo en un rato.");
    } finally {
      setLoadingMore(false);
    }
  };

  const toggle = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /** Llegó con `?item=<id>` desde la sidebar — busca esa novedad puntual (pidiendo más
   *  páginas si hace falta), la despliega, y hace scroll hasta ella. */
  useEffect(() => {
    if (!targetId || loading || autoHandledTargetRef.current === targetId) return;
    const found = news.find((n) => n.id === targetId);
    if (found) {
      autoHandledTargetRef.current = targetId;
      setExpandedIds((prev) => new Set(prev).add(targetId));
      requestAnimationFrame(() => {
        document.getElementById(`comunidad-news-${targetId}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    } else if (hasMore && !loadingMore) {
      loadMore();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetId, news, hasMore, loading, loadingMore]);

  return (
    <div className="min-h-full w-full bg-[#E6DBFA]">
      <div className="mx-auto flex max-w-[1440px] flex-col gap-6 px-4 py-6 md:px-8 md:py-10">

        <button
          type="button"
          onClick={() => navigate("/comunidad")}
          className="flex items-center gap-1.5 self-start text-sm font-semibold text-[#665090]"
        >
          <ArrowLeft size={16} />
          Volver a Comunidad
        </button>

        <h1 className="m-0 text-2xl font-extrabold text-[#2D2049]">{config.title}</h1>

        {loading ? (
          <div className="flex justify-center py-10"><CircularProgress /></div>
        ) : news.length === 0 ? (
          <p className="m-0 text-sm text-gray-500">Todavía no hay novedades cargadas.</p>
        ) : (
          <>
            <div className="flex flex-col gap-4">
              {news.map((n) => {
                const expanded = expandedIds.has(n.id);
                return (
                  <div
                    key={n.id}
                    id={`comunidad-news-${n.id}`}
                    className="rounded-[20px] bg-white p-4 shadow-[0_6px_20px_rgba(109,87,188,0.08)] scroll-mt-6 md:p-6"
                  >
                    <button
                      type="button"
                      onClick={() => toggle(n.id)}
                      className="flex w-full items-start gap-4 text-left"
                    >
                      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${config.iconBg}`}>
                        <Icon size={19} className={config.iconColor} />
                      </div>
                      <span className="flex-grow text-[15px] font-semibold leading-snug text-[#2D2049]">{n.title}</span>
                      <ChevronDown
                        size={18}
                        className={`mt-1 shrink-0 text-[#665090] transition-transform ${expanded ? "rotate-180" : ""}`}
                      />
                    </button>
                    {expanded && (
                      <div className="mt-3 flex flex-col gap-3 md:ml-[56px]">
                        {n.body &&
                          (isRichHtml(n.body) ? (
                            <RichContent html={n.body} className="text-sm text-gray-700" />
                          ) : (
                            <FormattedBody body={n.body} />
                          ))}
                        {/* Alto fijo (no ancho fijo): así una captura ancha y baja (ej. la
                         *  del buscador) se ve tan legible como una cuadrada — el tamaño
                         *  se ajusta a cuánta información hay que leer, no al espacio disponible. */}
                        {n.imageUrl && (
                          <img
                            src={n.imageUrl}
                            alt={n.title}
                            className="mx-auto h-auto max-h-[480px] w-auto max-w-full rounded-xl border border-[#E3D9FF] object-contain"
                          />
                        )}
                        {n.videoUrl && (
                          <iframe
                            src={toEmbedUrl(n.videoUrl)}
                            title={n.title}
                            className="aspect-video w-full rounded-xl border border-[#E3D9FF]"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        )}
                        {n.linkUrl && (
                          <a
                            href={n.linkUrl}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => {
                              // Artículo de la Home → se abre en una ventana encima de Comunidad.
                              const articleId = getHomeArticleId(n.linkUrl);
                              if (articleId) {
                                e.preventDefault();
                                setOpenArticleId(articleId);
                              }
                            }}
                            className="mx-auto inline-flex w-fit items-center gap-1.5 rounded-full bg-[#D4EF6F] px-4 py-2 text-xs font-bold text-[#2D2049] transition-transform hover:scale-[1.03]"
                          >
                            {n.linkLabel || "Ver más"}
                            <ExternalLink size={13} />
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {hasMore && (
              <Button
                variant="outline"
                className="self-center border-[#C9BCE8] text-[#665090]"
                onClick={loadMore}
                disabled={loadingMore}
              >
                {loadingMore ? "Cargando…" : "Cargar más"}
              </Button>
            )}
          </>
        )}

      </div>
      <HomeArticleDialog articleId={openArticleId} onClose={() => setOpenArticleId(null)} />
    </div>
  );
};

export default NewsArchiveContent;
